
import { useApiStore } from "@/stores/apiStore";
import { useAuthStore } from "@/stores/authStore";
import { useToastStore } from "@/stores/toastStore";
import moment from "moment-timezone";
import { FormatBytes } from "@/utils/Utilities";
import { defineComponent } from "vue";
import mapTable from "./mapTable.vue";
import { AxiosError } from "axios";
import HierarchySelector from "./hierarchy.vue";
import { useHierarchyStore } from "@/stores/hierarchyStore";
import { RawColumn } from "@/types/dataset";

const collarMappingVariables = {
	hole: "",
	easting: "",
	northing: "",
	elevation: "",
	depth: ""
};
type CollarMappingVariables = {
	[K in keyof typeof collarMappingVariables]: string[];
};

const collarAliases = {
	hole: ["hole id", "holeid", "hole_id", "hole-id"],
	easting: ["x"],
	northing: ["y"],
	elevation: ["z"],
	depth: ["length", "distance"]
} as CollarMappingVariables;

const surveyMappingVariables = {
	hole: "",
	depth: "",
	azimuth: "",
	dip: ""
};
type SurveyMappingVariables = {
	[K in keyof typeof surveyMappingVariables]: string[];
};

const surveyAliases = {
	hole: ["hole id", "holeid", "hole_id", "hole-id"],
	depth: ["length", "distance"],
	azimuth: [],
	dip: []
} as SurveyMappingVariables;

const plannedMappingVariables = {
	hole: "",
	easting: "",
	northing: "",
	elevation: "",
	depth: ""
};
type PlannedMappingVariables = {
	[K in keyof typeof plannedMappingVariables]: string[];
};

const plannedAliases = {
	hole: ["hole id", "holeid", "hole_id", "hole-id"],
	easting: ["x"],
	northing: ["y"],
	elevation: ["z"],
	depth: ["length", "distance"]
} as PlannedMappingVariables;

const trajectoryMappingVariables = {
	hole: "",
	depth: "",
	azimuth: "",
	dip: ""
};
type TrajectoryMappingVariables = {
	[K in keyof typeof trajectoryMappingVariables]: string[];
};

const trajectoryAliases = {
	hole: ["hole id", "holeid", "hole_id", "hole-id"],
	depth: ["length", "distance"],
	azimuth: [],
	dip: []
} as TrajectoryMappingVariables;

const intervalMappingVariables = {
	hole: "",
	from: "",
	to: ""
};
type IntervalMappingVariables = {
	[K in keyof typeof intervalMappingVariables]: string[];
};

const intervalAliases = {
	hole: ["hole id", "holeid", "hole_id", "hole-id"],
	from: [],
	to: []
} as IntervalMappingVariables;

const pointMappingVariables = {
	hole: "",
	depth: ""
};
type PointMappingVariables = {
	[K in keyof typeof pointMappingVariables]: string[];
};

const pointAliases = {
	hole: ["hole id", "holeid", "hole_id", "hole-id"],
	depth: ["length", "distance"]
} as PointMappingVariables;

const pointdataMappingVariables = {
	x: "",
	y: "",
	z: ""
};
type PointDataMappingAliases = {
	[K in keyof typeof pointdataMappingVariables]: string[];
};

const pointdataAliases = {
	x: ["easting"],
	y: ["northing"],
	z: ["elevation"]
} as PointDataMappingAliases;

const blockmodelMappingVariables = {
	xc: "",
	yc: "",
	zc: "",
	xinc: "",
	yinc: "",
	zinc: ""
};
type BlockmodelMappingAliases = {
	[K in keyof typeof blockmodelMappingVariables]: string[];
};

const blockmodelAliases = {
	xc: ["xc"],
	yc: ["yc"],
	zc: ["zc"],
	xinc: ["xinc"],
	yinc: ["yinc"],
	zinc: ["zinc"]
} as BlockmodelMappingAliases;

export default defineComponent({
	name: "import-dataset-mapping",
	components: {
		mapTable,
		HierarchySelector
	},
	setup() {
		const api = useApiStore();
		const toast = useToastStore();
		const authStore = useAuthStore();
		const hierarchyStore = useHierarchyStore();
		const formatBytes = FormatBytes;
		return {
			hierarchyStore,
			moment,
			api,
			toast,
			authStore,
			formatBytes
		};
	},
	props: {
		maps: {
			type: Array,
			default: () => []
		},
		type: {
			type: String,
			default: "drillhole"
		},
		meta: {
			type: Object,
			default: () => {
				return {};
			}
		}
	},
	data() {
		return {
			step: 0,
			activeFile: 0,
			delimiter: ",",
			datastartrow: 2,
			nanSymbol: "",
			tempMaps: [] as any,
			dataMapping: [
				{
					holeId: "",
					northing: "",
					easting: "",
					elevation: "",
					depth: "",
					from: "",
					to: ""
				}
			] as any[],
			loading: true,
			cancelDialog: false,
			verified: false,
			hasError: false,
			creating: false,
			transOutNext: false,
			transInNext: false,
			transOutPrev: false,
			transInPrev: false,
			transitioningPrev: false,
			showHierarchySelector: false,
			errors: {} as any,
			hierarchySelectedColumns: [] as string[],
			getMetadataDebounceTimeout: 0,
			attributesMap: null,
			noVariablesSelected: false
		};
	},
	mounted() {
		this.tempMaps = [...this.maps];

		this.tempMaps = this.tempMaps.map((item: any, i: number) => {
			return {
				...item,
				tableData: this.generateTableData(i),
				variableMapping: this.generateVariables(item.type)
			};
		});
		this.dataMapping = this.maps.map(() => {
			return {
				holeId: "",
				northing: "",
				easting: "",
				elevation: "",
				depth: "",
				from: "",
				to: ""
			};
		});
		// will load attributesMap
		this.updateMetadataConfigForFile();
		this.loading = false;
	},
	methods: {
		updateVariableMapping(e: InputEvent | string, type: string) {
			// handle changeEvent from submitting the text input we allow search from
			if (typeof e != "string") {
				const target = e.target as HTMLInputElement;
				const filteredColumns = this.availableColumns.filter(
					(column: { text: string; value: string }) =>
						column.text.toLowerCase().includes(target.value.toLowerCase())
				);
				if (filteredColumns.length === 1) {
					e = filteredColumns[0].text as string;
				} else {
					// if we don't have a single match, no further action needed; if we do, continue as normal. The select field value is set internally in the <imdex-select /> element
					return;
				}
			}
			const column = this.tempMaps[this.activeFile].tableData.columns.findIndex(
				(item: any) => item.name == e
			);
			if (type == "hole" || type == "holeId") {
				this.tempMaps[this.activeFile].tableData.columns[column].statsDataType =
					"categorical";
				// this.hierarchySelectedColumns = [this.tempMaps[this.activeFile].tableData.columns[column]?.name] // if hole name varies, we can't allow it as a selection
			} else {
				// For: x, y, z, depth, from, to, azimuth & dip (not specified  as anything but holeId will use continuous only)
				this.tempMaps[this.activeFile].tableData.columns[column].statsDataType =
					"continuous";
			}
			this.tempMaps[this.activeFile].tableData.columns[column].selected = true;
		},
		async finishedMapping() {
			const v = this.verify();
			if (v) {
				if (!this.showHierarchySelector) {
					if (this.type == "drillhole") {
						this.showHierarchySelector = true;
						return;
					}
				}
				this.creating = true;
				try {
					let postData = {
						...this.meta
					} as any;

					for (let i = 0; i < this.tempMaps.length; i++) {
						//  update selected fields, nanValues, categorical vs continuous & units (not relevant to columns, so these can wait until we generate)
						const { data } = await this.api.put({
							endpoint: `/dataset-metadata/update`,
							post: { ...this.tempMaps[i].metadata }
						});

						const type = this.tempMaps[i].type;
						if (type == "point" || type == "interval") {
							if (postData[type] == null) postData[type] = [];
							postData[type].push({
								id: this.tempMaps[i].metadata.id,
								metadataConfigId: data.metadataConfigId,
								...this.tempMaps[i].variableMapping
							});
						} else {
							postData[type] = {
								id: this.tempMaps[i].metadata.id,
								metadataConfigId: data.metadataConfigId,
								...this.tempMaps[i].variableMapping
							};
						}
					}

					// handle hierarchy column selection, if it applies
					let chunk_cols: string[] = [];
					let hierarchy_cols: string[] = [];
					if (this.type == "drillhole" || this.type == "point") {
						if (this.hierarchySelectedColumns.length > 0) {
							hierarchy_cols = [this.hierarchySelectedColumns[0]];
							chunk_cols = this.hierarchySelectedColumns.slice(1); // all but the first element
						} else {
							hierarchy_cols = [];
							chunk_cols = [];
						}
					}

					if (this.type == "drillhole") {
						postData = {
							...postData,
							intervals: postData.interval,
							points: postData.point,
							collar: { ...postData.collar, hierarchy_cols, chunk_cols }
						};

						await this.api.post({
							endpoint: `/dataset-drillhole/create`,
							post: postData
						});
					} else if (this.type == "point") {
						await this.api.post({
							endpoint: `/dataset-points/create`,
							post: {
								folderPath: postData.folderPath,
								groupName: postData.groupName,
								name: postData.name,
								id: postData.pointdata.id,
								x_col: postData.pointdata.x,
								y_col: postData.pointdata.y,
								z_col: postData.pointdata.z,
								metadataConfigId: postData.pointdata.metadataConfigId,
								hierarchy_cols,
								chunk_cols
							}
						});
					} else if (this.type === "blockmodel") {
						await this.api.post({
							endpoint: "/dataset-blockmodel/create",
							post: {
								id: postData.blockmodel.id,
								...postData,
								...postData.blockmodel
							}
						});
					}
					this.creating = false;
					this.toast.toast({
						type: "success",
						title: this.$t("import.mapping.toastDsCreate"),
						message: "",
						dismissable: false
					});
					this.$emit("finished");
					this.hierarchyStore.fetchHierarchy();
				} catch (e) {
					if (e instanceof AxiosError) {
						this.creating = false;
						this.toast.toast({
							type: "error",
							title: e.response?.data.message,
							message: "",
							dismissable: false
						});
					}
				}
			}
		},
		updateTableMap(map: any) {
			const noVariablesSelected = map.columns.every((column: RawColumn) => !column.selected);
			this.hasError = noVariablesSelected;
			this.noVariablesSelected = noVariablesSelected;
			this.tempMaps[this.activeFile].metadata.columns = map.columns;
		},
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		async refreshMetadata(_potentialEvent: any = null, force = false) {
			if (this.loading) {
				// if already loading, don't make another request and queue only one network request at a time
				this.refreshMetadataDebounce();
				return;
			}
			if (
				!force &&
				this.tempMaps[this.activeFile].metadata.delimiter === this.delimiter &&
				this.nanSymbol ===
					JSON.parse(this.tempMaps[this.activeFile].metadata.nanValues)?.[0] &&
				this.datastartrow - 1 == this.tempMaps[this.activeFile].metadata.skipHeaderLines
			) {
				this.loading = false;
				// if nothing has changed, don't make a request
				return;
			}
			this.loading = true;

			this.errors["dataStartRow"] = null;

			if (this.delimiter == "") {
				this.delimiter = ",";
			}

			if (
				this.delimiter.length > 1 ||
				(this.delimiter !== " " && !this.delimiter.trim()) ||
				/^[A-Za-z0-9]*$/.test(this.delimiter)
			) {
				this.hasError = true;
				this.errors["delimiter"] = true;
				this.loading = false;
				return;
			} else {
				this.hasError = false;
				this.errors["delimiter"] = false;
			}

			if (!this.datastartrow) {
				this.datastartrow = 2;
				this.loading = false;
				return;
			}

			try {
				const { data } = await this.api.put({
					endpoint: `/dataset-metadata/update`,
					post: {
						...this.tempMaps[this.activeFile].metadata,
						delimiter: this.delimiter,
						skipHeaderLines: Number(this.datastartrow - 1), // Decrementing 1 from datastartrow to adjust for 0-based numbering. The UI displays 1 based indexing
						nanValues: JSON.stringify([this.nanSymbol]) // stringify is temporary while this is a single value
					}
				});

				if (data.errorMsg) {
					setTimeout(() => {
						this.loading = false;
						this.refreshMetadata(true);
					}, 5000);
					return;
				}

				this.tempMaps[this.activeFile].metadata = data;
				this.tempMaps[this.activeFile] = {
					...this.tempMaps[this.activeFile],
					tableData: this.generateTableData(this.activeFile),
					variableMapping: this.generateVariables(this.tempMaps[this.activeFile].type)
				};
				this.autoAssignMappingWithAliases(); // should be redundant but input values aren't assigning properly without setting explicitly
				this.$nextTick(() => {
					this.loading = false;
				});
			} catch (e: any) {
				this.loading = false;
				this.toast.toast({
					title: this.$t("import.mapping.toastErrorGetMetadata"),
					type: "error",
					dismissable: false,
					message: ""
				});
			}
		},
		refreshMetadataDebounce() {
			if (this.getMetadataDebounceTimeout) {
				clearTimeout(this.getMetadataDebounceTimeout);
			}
			this.getMetadataDebounceTimeout = setTimeout(this.refreshMetadata, 1000);
		},
		confirmCancel() {
			this.$emit("cancel");
			this.cancelDialog = false;
			this.showHierarchySelector = false;
		},
		verify() {
			this.errors = {};

			const keys = Object.keys(this.tempMaps[this.activeFile].variableMapping).filter(
				(item: any) => {
					return this.tempMaps[this.activeFile].variableMapping[item] == "";
				}
			);
			this.verified = true;

			if (
				this.delimiter.length > 1 ||
				(this.delimiter !== " " && !this.delimiter.trim()) ||
				/^[A-Za-z0-9]*$/.test(this.delimiter)
			) {
				this.hasError = true;
				this.errors["delimiter"] = true;
			}

			// I think this is legacy; we don't need any values, an empty array or an empty string in an array are fine
			// if (this.nanSymbol.length && !this.nanSymbol.trim()) {
			// 	this.hasError = true;
			// 	this.errors["nanSymbol"] = true;
			// }

			if (!this.datastartrow) {
				this.hasError = true;
				this.errors["dataStartRow"] = true;
			}

			if (keys.length > 0) {
				this.hasError = true;
				this.errors["variables"] = true;
			}

			if (Object.keys(this.errors).length) {
				return false;
			}

			this.hasError = false;
			return true;
		},
		next() {
			const ver = this.verify();
			if (ver) {
				this.transOutNext = true;
				this.verified = false;
				if (this.activeFile < this.maps.length - 1) {
					setTimeout(() => {
						this.activeFile += 1;
						this.updateMetadataConfigForFile();
						this.transOutNext = false;
						this.transInNext = true;
						setTimeout(() => {
							this.transInNext = false;
						}, 300);
					}, 300);
				}
			}
		},
		prev() {
			this.transOutPrev = true;
			setTimeout(() => {
				if (this.showHierarchySelector) this.showHierarchySelector = false;
				else {
					this.activeFile -= 1;
					this.updateMetadataConfigForFile();
				}
				this.transOutPrev = false;
				this.transInPrev = true;
				setTimeout(() => {
					this.transInPrev = false;
				}, 300);
			}, 300);
		},
		updateMetadataConfigForFile() {
			const fileMetadata = this.tempMaps[this.activeFile].metadata;
			this.delimiter = fileMetadata.delimiter;
			this.datastartrow = fileMetadata.skipHeaderLines + 1; // incrementing this.datastartrow because we (skipHeaderLines) use 0 based numbering while the UI uses 1 based

			// this.nanSymbol = fileMetadata.nanValues
			const nanValues = JSON.parse(fileMetadata.nanValues);
			if (nanValues.length > 0) this.nanSymbol = nanValues[0];
			else this.nanSymbol = "";

			this.autoAssignMappingWithAliases();

			if (this.isGasFile) {
				this.loadAttributes();
			}

			return;
		},
		generateVariables(type: string) {
			switch (type) {
				case "collar":
					return { ...collarMappingVariables };
				case "survey":
					return { ...surveyMappingVariables };
				case "planned":
					return { ...plannedMappingVariables };
				case "trajectory":
					return { ...trajectoryMappingVariables };
				case "interval":
					return { ...intervalMappingVariables };
				case "point":
					return { ...pointMappingVariables };
				case "blockmodel":
					if (this.fileExtension == "csv" || this.fileExtension == "dm")
						return { ...blockmodelMappingVariables };
					else return {};
				case "shape":
					return {}; // unused
				case "pointdata":
					return { ...pointdataMappingVariables };
				default:
					return {};
			}
		},
		autoAssignMappingWithAliases() {
			const type = this.tempMaps[this.activeFile].type;

			let aliases: Record<string, string[]> = {};
			switch (type) {
				case "collar":
					aliases = collarAliases;
					break;
				case "survey":
					aliases = surveyAliases;
					break;
				case "planned":
					aliases = plannedAliases;
					break;
				case "trajectory":
					aliases = trajectoryAliases;
					break;
				case "interval":
					aliases = intervalAliases;
					break;
				case "point":
					aliases = pointAliases;
					break;
				case "blockmodel":
					aliases = blockmodelAliases;
					break;
				// case "shape": // no need for aliases with no user mapping
				//     aliases = shapeAliases;
				//     break;
				case "pointdata":
					if (
						this.fileExtension == "gas" &&
						this.tempMaps[this.activeFile].metadata.defaultColumns
					)
						aliases = Object.fromEntries(
							// core-services returns a single string, but other aliases use a string[] as they have multiple options
							// we also cast to lowercase as the aliases are case insensitive - this can be changed if needed
							Object.entries(
								this.tempMaps[this.activeFile].metadata.defaultColumns
							).map(([key, value]) => [key, [(value as string).toLowerCase()]])
						);
					else aliases = pointdataAliases;

					break;
				default:
					break;
			}

			// automatically assign columns if the name matches our labels, or one of the aliases for the labels
			for (const column of this.tempMaps[this.activeFile].metadata.columns) {
				let variableMappingKeyToAssign = "";
				const columnMatch = Object.keys(
					this.tempMaps[this.activeFile].variableMapping
				).find((variableMappingKey) => {
					if (
						variableMappingKey == column.name.toLowerCase() ||
						(aliases[variableMappingKey] &&
							aliases[variableMappingKey].includes(column.name.toLowerCase()))
					) {
						variableMappingKeyToAssign = variableMappingKey;
						return true;
					}
				});
				if (columnMatch) {
					this.tempMaps[this.activeFile].variableMapping[variableMappingKeyToAssign] =
						column.name;
				}
			}
		},
		loadAttributes() {
			this.attributesMap = this.tempMaps[this.activeFile].metadata.attributesMap;
		},
		generateTableData(i: number) {
			const columnsMetadata: RawColumn[] = (this.tempMaps[i] as any).metadata.columns;
			// A lookup object for type conversion
			const columnTypeMap: { [key: string]: string } = {
				int: "continuous",
				float: "continuous",
				string: "categorical"
			};
			// Function to get column type
			const getColumnType = (type: string) => columnTypeMap[type] || type;
			const columns = columnsMetadata.map((item: any) => {
				return {
					name: item.name,
					_id: item._id,
					selected: item?.selected ?? true,
					dataType: item.dataType,
					statsDataType: item.statsDataType ?? getColumnType(item.dataType),
					units: item.units ?? "None"
				};
			});

			// Populate table data for user visualization
			type TableRow = { [key: string]: any };
			const tableData: TableRow[] = columnsMetadata[0].sampleData.map(() => ({}));

			columnsMetadata.forEach((column) => {
				column.sampleData.forEach((cellValue, index) => {
					tableData[index][column.name] = cellValue;
				});
			});

			return {
				tableData: tableData,
				options: {
					columns: {}
				},
				columns
			};
		},
		updateHierarchySelectedColumns(newValue: string[]) {
			this.hierarchySelectedColumns = [...newValue, this.tempMaps[0].variableMapping.hole];
		},
		generateTooltipContent(
			item:
				| "hole"
				| "easting"
				| "northing"
				| "elevation"
				| "depth"
				| "azimuth"
				| "dip"
				| "from"
				| "to"
				| "x"
				| "y"
				| "z"
				| "xc"
				| "yc"
				| "zc"
				| "xinc"
				| "yinc"
				| "zinc"
		) {
			switch (item) {
				case "hole":
					if (this.tempMaps[this.activeFile].type === "collar")
						return this.$t("import.mapping.mapHoleCollarTooltip");
					// this.tempMaps[this.activeFile].type == "survey", "points", "interval"
					else return this.$t("import.mapping.mapHoleOtherTooltip");
				case "easting":
					if (this.tempMaps[this.activeFile].type === "collar")
						return this.$t("import.mapping.mapEastCollarTooltip");
					else return this.$t("import.mapping.mapErrorTooltip");
				case "northing":
					if (this.tempMaps[this.activeFile].type === "collar")
						return this.$t("import.mapping.mapNorthCollarTooltip");
					else return this.$t("import.mapping.mapErrorTooltip");
				case "elevation":
					if (this.tempMaps[this.activeFile].type === "collar")
						return this.$t("import.mapping.mapElevCollarTooltip");
					else return this.$t("import.mapping.mapErrorTooltip");
				case "depth":
					if (this.tempMaps[this.activeFile].type === "collar")
						return this.$t("import.mapping.mapDepthCollarTooltip");
					else if (this.tempMaps[this.activeFile].type === "survey")
						return this.$t("import.mapping.mapDepthSurveyTooltip");
					else if (this.tempMaps[this.activeFile].type === "point")
						return this.$t("import.mapping.mapDepthPointTooltip");
					else return this.$t("import.mapping.mapErrorTooltip");
				case "azimuth":
					return this.$t("import.mapping.mapAzimuthTooltip");
				case "dip":
					return this.$t("import.mapping.mapDipTooltip");
				case "from":
					return this.$t("import.mapping.mapFromTooltip");
				case "to":
					return this.$t("import.mapping.mapToTooltip");
				case "x":
					return this.$t("import.mapping.mapXTooltip");
				case "y":
					return this.$t("import.mapping.mapYTooltip");
				case "z":
					return this.$t("import.mapping.mapZTooltip");
				case "xc":
					return this.$t("import.mapping.mapBlockmodelTooltip") + ": xc";
				case "yc":
					return this.$t("import.mapping.mapBlockmodelTooltip") + ": yc";
				case "zc":
					return this.$t("import.mapping.mapBlockmodelTooltip") + ": zc";
				case "xinc":
					return this.$t("import.mapping.mapBlockmodelTooltip") + ": xinc";
				case "yinc":
					return this.$t("import.mapping.mapBlockmodelTooltip") + ": yinc";
				case "zinc":
					return this.$t("import.mapping.mapBlockmodelTooltip") + ": zinc";
				default:
					return this.$t("import.mapping.mapInvalidTooltip");
			}
		}
	},
	computed: {
		hierarchyCategoricalColumns() {
			if (
				this.tempMaps[this.tempMaps.findIndex((item: any) => item.type == "pointdata")] !=
				null
			) {
				return (
					this.tempMaps[
						this.tempMaps.findIndex((item: any) => item.type == "pointdata")
					] as any
				).metadata.columns.filter((item: RawColumn) => {
					return item.statsDataType == "categorical";
				});
			} else if (this.tempMaps.findIndex((item: any) => item.type == "collar") >= 0) {
				// only supports drillholes; points columns should be parsed from the previous conditional branch
				// when only collar, we can just return all of its columns
				return this.tempMaps[0].metadata.columns.filter((item: RawColumn) => {
					return item.statsDataType == "categorical";
				});
			} else return [];
		},
		availableColumns(): any[] {
			if (this.tempMaps.length > 0) {
				return (this.tempMaps[this.activeFile] as any).metadata.columns.map((item: any) => {
					return {
						text: item.name,
						value: item.name,
						sampleData: item.sampleData,
						type: item.dataType
					};
				});
			} else {
				return [];
			}
		},
		steps() {
			const formatTypeName = (name: string) => {
				if (name == "collar") {
					return this.$t("import.mapping.fileTypeCollar");
				}
				if (name == "survey") {
					return this.$t("import.mapping.fileTypeSurvey");
				}
				if (name == "planned") {
					return this.$t("import.mapping.fileTypePlanned");
				}
				if (name == "trajectory") {
					return this.$t("import.mapping.fileTypeTraj");
				}
				if (name == "interval") {
					return this.$t("import.mapping.fileTypeInt");
				}
				if (name == "point") {
					return this.$t("import.mapping.fileTypePoint");
				}
				if (name == "blockmodel") {
					return this.$t("import.mapping.fileTypeBlockmodel");
				}
				if (name == "pointdata") {
					return this.$t("import.mapping.fileTypePoint");
				}
				if (name == "shape") {
					return this.$t("import.mapping.fileTypeShape");
				}
				return "Non Listed File Type";
			};

			return this.maps.map((item: any) => {
				return {
					type: item.type,
					name: formatTypeName(item.type)
				};
			});
		},
		fileExtension() {
			const fileExtension = this.tempMaps?.[this.activeFile]?.metadata?.fileName
				.split(".")
				.pop();
			return fileExtension;
		},
		skipMapping() {
			const fileExtension = this.fileExtension;
			return fileExtension === "dm" || fileExtension === "dat";
		},
		isGasFile() {
			const fileExtension = this.fileExtension;
			return fileExtension === "gas";
		}
	}
});
